/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { internalSessionIssuersGet } from "../funcs/internalSessionIssuersGet.js";
import { internalSessionIssuersRotate } from "../funcs/internalSessionIssuersRotate.js";
import { internalSessionIssuersSetEnabled } from "../funcs/internalSessionIssuersSetEnabled.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { InternalSessionIssuer } from "../models/components/internalsessionissuer.js";
import {
  GetInternalSessionIssuerRequest,
  GetInternalSessionIssuerSecurity,
} from "../models/operations/getinternalsessionissuer.js";
import {
  RotateInternalSessionIssuerRequest,
  RotateInternalSessionIssuerSecurity,
} from "../models/operations/rotateinternalsessionissuer.js";
import {
  SetInternalSessionIssuerEnabledRequest,
  SetInternalSessionIssuerEnabledSecurity,
} from "../models/operations/setinternalsessionissuerenabled.js";
import { unwrapAsync } from "../types/fp.js";

export class InternalSessionIssuers extends ClientSDK {
  /**
   * get internalSessionIssuers
   *
   * @remarks
   * Read the organization's internal session issuer: its state, the issuer URI to register with supported services and its published keys. Requires org:read.
   */
  async get(
    request?: GetInternalSessionIssuerRequest | undefined,
    security?: GetInternalSessionIssuerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<InternalSessionIssuer> {
    return unwrapAsync(internalSessionIssuersGet(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * rotate internalSessionIssuers
   *
   * @remarks
   * Rotate the issuer's signing key without changing its URI. A routine rotation takes two calls with the same active_kid: the first publishes a new key and returns rotation_ready_at, the second activates it after that time. The hourly sweep also activates a ready key on its own while the issuer is enabled and not paused, so a later second call may find the rotation already done and change nothing; a paused issuer can still be activated with the second call. With compromised set, every current key is revoked and withdrawn from the key set first, leaving the issuer keyless; the revoked keys' signing material is then disabled and, while the issuer is rolled out and the organization has a verified email domain, a fresh key activated. If either later step fails the issuer stays keyless or reports kms_cleanup_pending, and repeating the call retries it. Repeats naming a key that a compromised rotation already replaced change nothing; naming a key the sweep revoked after a routine rotation still contains its successor. Requires org:admin; a routine rotation also needs an enabled issuer, while a compromised rotation only needs an existing issuer and its revocation is never rate limited.
   */
  async rotate(
    request: RotateInternalSessionIssuerRequest,
    security?: RotateInternalSessionIssuerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<InternalSessionIssuer> {
    return unwrapAsync(internalSessionIssuersRotate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * setEnabled internalSessionIssuers
   *
   * @remarks
   * Enable or disable signing with the organization's internal session issuer. Enabling provisions the issuer with a dedicated Speakeasy-managed signing key the first time; it does not configure trust at any service. Disabling keeps the issuer URI and its published keys. Requires org:admin; enabling also needs a verified email domain and the issuer rollout, while disabling never does.
   */
  async setEnabled(
    request: SetInternalSessionIssuerEnabledRequest,
    security?: SetInternalSessionIssuerEnabledSecurity | undefined,
    options?: RequestOptions,
  ): Promise<InternalSessionIssuer> {
    return unwrapAsync(internalSessionIssuersSetEnabled(
      this,
      request,
      security,
      options,
    ));
  }
}
